import React, { useState, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import EmptyScreen from "./EmptyScreen";
import SavedServicesScreen from "./SavedServicesScreen";
import NewServiceModal from "./NewServiceModal";
import { useAppStore } from "../../../../providers/AppStore";
import { useSelector } from "react-redux";
import { RootStateServiceLogicBuilder } from "../../store/service-logic-builder-slice";

const ServiceBuilder: React.FC = () => {
  const { loadData } = useOutletContext<{ loadData: () => Promise<void> }>();
  const { savedServices, isLoading } = useSelector(
    (state: RootStateServiceLogicBuilder) => ({
      savedServices: state.serviceLogicBuilder.savedServices,
      isLoading: state.serviceLogicBuilder.loading,
    })
  );
  const [openModal, setOpenModal] = useState(false);
  const [editingService, setEditingService] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [serviceToSelect, setServiceToSelect] = useState<any>(null);
  const [serviceFilter, setServiceFilter] = useState("");
  const [serviceTypeFilter, setServiceTypeFilter] = useState<number | null>(
    null
  );
  const { ASSetOpenSnackBar, ASSetSnackBarMessage, ASSetTypeSnackBar } =
    useAppStore();

  // Active (non-soft-deleted) services. Used both as the input to the
  // search/type filter below AND as the gate for the EmptyScreen render
  // — so a user whose every service has been deleted (all `active !== 1`)
  // sees the onboarding empty state instead of `SavedServicesScreen`
  // with zero rows.
  const activeServices = useMemo(
    () => savedServices.filter((s) => s.active === 1),
    [savedServices]
  );

  // Filter services based on search term and service type
  const filteredServices = useMemo(() => {
    return activeServices.filter((service) => {
      // Search filter
      const searchMatch =
        service.name?.toLowerCase().includes(serviceFilter.toLowerCase()) ||
        service.description
          ?.toLowerCase()
          .includes(serviceFilter.toLowerCase());

      // Service type filter
      const typeMatch =
        !serviceTypeFilter || service.serviceType === serviceTypeFilter;

      return searchMatch && typeMatch;
    });
  }, [activeServices, serviceFilter, serviceTypeFilter]);

  const handleServiceSaved = async (savedService?: any) => {
    // Set the service to be selected after data loads
    if (savedService) {
      setServiceToSelect(savedService);
    }

    // Refresh the services list
    await loadData();

    // Show success snackbar (modal is already closed by NewServiceModal)
    ASSetSnackBarMessage({
      title: "Success",
      message: "Service saved successfully!",
    });
    ASSetTypeSnackBar("success");
    ASSetOpenSnackBar(true);
  };

  return (
    <>
      {activeServices.length === 0 && !isLoading ? (
        <EmptyScreen type="service" setOpenModal={setOpenModal} />
      ) : (
        <SavedServicesScreen
          savedServices={filteredServices}
          setOpenModal={setOpenModal}
          serviceFilter={serviceFilter}
          setServiceFilter={setServiceFilter}
          serviceTypeFilter={serviceTypeFilter}
          setServiceTypeFilter={setServiceTypeFilter}
          loadData={loadData}
          setEditingService={setEditingService}
          setIsEditMode={setIsEditMode}
          serviceToSelect={serviceToSelect}
          onServiceSelected={() => setServiceToSelect(null)}
        />
      )}
      <NewServiceModal
        openModal={openModal}
        handleClose={() => {
          setOpenModal(false);
          setEditingService(null);
          setIsEditMode(false);
        }}
        onServiceSaved={handleServiceSaved}
        editingService={editingService}
        isEditMode={isEditMode}
      />
    </>
  );
};

export default ServiceBuilder;
